import { useEffect, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import { Link, withRouter } from 'react-router-dom'
import { Col, Row, Container } from 'reactstrap'
import { compose } from 'redux'
import { deleteCartItem, dispatchSweetAlert, clearSweetAlert, updateCartItems } from '../../actions'
import { createCheckoutSessionToken } from '../../redux/storeConfig/slices/slice.checkoutSessionToken'
import { createShippingGroupConfiguration } from '../../redux/storeConfig/slices/slice.checkoutRates'
import { fetchIntegrations } from '../../redux/storeConfig/slices/slice.integrations'
import { isShipperHQActive } from '../../util/integrations'
import CheckoutAddressSelection from '../Checkout/CheckoutAddressSelection'
import { setCheckoutErrorMessage, restoreCheckoutSessionFromToken, clearGuestAddresses } from '../Checkout/checkoutFunctions'
import OrderSummary from '../Checkout/CheckoutOrderSummary'
import GuestCheckoutPanel from '../Checkout/GuestCheckoutPanel'
import { useUserIdentifier } from '../Checkout/hooks/useUserIdentifier'
import { buildShippingParams } from '../Checkout/services/shippingParamsBuilder'
import {
  productHasError,
  resolveBundleProductErrorMessage,
  cartItemsRequireCheckForApproval,
  returnCartItemsRequireApprovalError
} from './cartFunctions'
import CartItemsList from './CartItemsList'
import ProductionTimeBanner from './ProductionTimeBanner'

const errorMessages = {
  maxQtyReached: 'You have exceeded the maximum quantity for a product.'
}

// TODO: refactor this component - Currently compromised by overuse of useEffect, use hooks for redux and react-router
const Cart = props => {
  const { hasIdentifier } = useUserIdentifier()

  useEffect(() => {
    const initializeCheckoutSession = async () => {
      if (props.cartItems.length > 0) {
        // For guest users, clear any stale guest addresses before restoring from token
        if (!props.currentUser?.id) {
          await props.clearGuestAddresses()
        }
        const tokenResult = await props.createCheckoutSessionToken()
        // Restore all checkout session data (addresses, billing, etc.) from token if available
        if (tokenResult) {
          await props.restoreCheckoutSessionFromToken(tokenResult)
        }
      }
    }
    initializeCheckoutSession()
  }, [props.cartItems.length])

  useEffect(() => {
    // apply credits according to selections
    const cartItemsWithAppliedCredits = applyCoopCreditsToCartItems(props.cartItems)
    props.updateCartItems(cartItemsWithAppliedCredits)

    // Fetch integrations data
    props.fetchIntegrations()
  }, [])

  useEffect(() => {
    const firstError = Object.entries(props.errors).find(([key, val]) => val === true)
    if (firstError) {
      const errorMessage = errorMessages[firstError[0]]
      props.setCheckoutErrorMessage(errorMessage)
    } else {
      props.setCheckoutErrorMessage('')
    }
  }, [props.errors])

  // check cart items for errors if on main cart page
  useEffect(() => {
    const productWithError = props.cartItems.find(ci => productHasError(ci))
    if (productWithError) {
      let errorMessage
      if (!productWithError.error) {
        // error is on child product
        errorMessage = resolveBundleProductErrorMessage(productWithError.bundle.products)
      } else {
        errorMessage = productWithError.error.message
      }
      // show error to user, and allow them to either:
      // - delete item
      // - delete item and go to product page
      const propsForSweetAlert = {
        confirmBtnText: 'Remove & view product',
        onConfirm: () => handleConfirmError(productWithError),
        alertMessage: errorMessage,
        cancelBtnText: 'Remove',
        onCancel: () => handleCancelError(productWithError),
        confirmBtnCssClass: 'mf-primary-btn',
        cancelBtnCssClass: 'mf-outline-btn alert-width-btn',
        closeOnClickOutside: false
      }
      props.dispatchSweetAlert(propsForSweetAlert)
    }
  }, [props.cartItems])

  // Check if cart items contain both requires approval and does not require approval
  useEffect(() => {
    if (
      props.cartItems &&
      props.cartItems.length > 0 &&
      cartItemsRequireCheckForApproval(null, props.cartItems, props.currentLocation)
    ) {
      props.returnCartItemsRequireApprovalError(props.cartItems, null)
    }
  }, [props.cartItems])

  // Create a stable hash of multi-address selections for dependency tracking
  const multiAddressSelectionsHash = useMemo(() => {
    if (!props.checkout?.multiAddressSelection?.length) return null
    return props.checkout.multiAddressSelection
      .map(s => `${s.cartItemId}:${s.address?.id || 'none'}`)
      .sort()
      .join('|')
  }, [props.checkout?.multiAddressSelection])

  // Helper function to create shipping configuration
  const createConfiguration = useCallback(() => {
    const isShipperHQ = isShipperHQActive(props.integrations?.data)
    const shippableItems = props.cartItems?.filter(item => item.shippable_type !== 'Digital Delivery') || []

    // Only create configuration for non-ShipperHQ clients
    if (isShipperHQ) return
    if (!hasIdentifier) return
    if (!props.cartItems?.length) return
    if (shippableItems.length === 0) return

    // For multi-address, check if all items have selections
    const multiAddressEnabled = props.portal.multiple_address_shipping && props.currentUser?.id
    if (multiAddressEnabled) {
      const eachItemHasSelection = props.cartItems.every(cartItem =>
        props.checkout?.multiAddressSelection?.map(s => s.cartItemId).includes(cartItem.cart_item_id)
      )
      if (!eachItemHasSelection) return
    }

    // Build params using centralized service
    const params = buildShippingParams({
      selectedAddress: props.checkout?.selectedCustomerAddress,
      cartItems: shippableItems,
      multiAddressSelection: props.checkout?.multiAddressSelection,
      isMultiAddressEnabled: multiAddressEnabled
    })

    if (params) {
      props.createShippingGroupConfiguration(params)
    }
  }, [
    hasIdentifier,
    props.integrations?.data,
    props.cartItems,
    props.checkout?.multiAddressSelection,
    props.checkout?.selectedCustomerAddress,
    props.currentUser?.id,
    props.portal.multiple_address_shipping,
    props.createShippingGroupConfiguration
  ])

  // Initial configuration on page load
  useEffect(() => {
    createConfiguration()
  }, [createConfiguration])

  // Recreate configuration when address selections change
  useEffect(() => {
    if (!props.integrations?.data || !hasIdentifier || !props.cartItems?.length) {
      return
    }

    createConfiguration()
  }, [
    hasIdentifier,
    multiAddressSelectionsHash,
    props.checkout?.selectedCustomerAddress?.id,
    createConfiguration,
    props.integrations?.data,
    props.cartItems?.length
  ])

  const handleConfirmError = cartItemWithError => {
    props.deleteCartItem(cartItemWithError.cart_item_id)
    props.history.push(`/product/${cartItemWithError.product_id}/${props.portal.id}/${props.currentLocation?.id}`)
  }
  const handleCancelError = cartItemWithError => {
    props.deleteCartItem(cartItemWithError.cart_item_id)
    props.clearSweetAlert()
  }

  const clearAppliedCredits = cartItems => {
    return cartItems.map(cartItem => {
      return {
        ...cartItem,
        priceData: {
          ...cartItem.priceData,
          appliedCredits: {}
        }
      }
    })
  }

  // accepts cart items, returns cart items with updated priceData containing applied credits
  const applyCoopCreditsToCartItems = cartItems => {
    // first, clear all discounts to start from a clean slate
    cartItems = clearAppliedCredits(cartItems)
    // get the desired credits to be used
    let coopCreditsToBeApplied = props.userCredits.coop_credit.remaining

    const result = cartItems.map(cartItem => {
      const newPriceData = {
        ...cartItem.priceData,
        appliedCredits: {}
      }

      // if there is a discount applied to subtotal, caluclate amount to apply to each product
      // (this should always be 0 if on cart page)
      const perProductDiscount = 0

      // * Co-Op *
      // If user wants to apply coop credits and priceData has coop_deduction_cents, (is eligible)
      // apply as many coopcredits as possible
      if (coopCreditsToBeApplied && newPriceData.coop_deduction_cents) {
        // get the total amount possible that can be applied
        let applicableCoopDeduction = newPriceData.coop_deduction_cents

        // If a discount is present, use the adjusted price and coop deduction
        if (perProductDiscount) {
          applicableCoopDeduction = newPriceData.adjusted_coop_deduction_cents
        }

        applicableCoopDeduction = applicableCoopDeduction * cartItem.quantity
        // if we have enough for the full deduction, apply it
        if (coopCreditsToBeApplied >= applicableCoopDeduction) {
          coopCreditsToBeApplied -= applicableCoopDeduction
          newPriceData.appliedCredits.coop = applicableCoopDeduction
        } else {
          // else apply our remaining credit
          newPriceData.appliedCredits.coop = coopCreditsToBeApplied
          coopCreditsToBeApplied -= coopCreditsToBeApplied
        }
      }

      return {
        ...cartItem,
        priceData: newPriceData
      }
    })
    return result
  }

  // If Cart is empty, show empty cart page:
  if (props.cartItems.length === 0 && props.cart_loading === false) {
    return (
      <div className="section-pad text-center">
        <div className="mb-30">
          <img src={require('../../assets/images/empty-cart.png')} alt="shop-cart" />
        </div>
        <h4>Your Shopping Cart is empty.</h4>
        <Link to="/shop" className="text-capitalize">
          Go Shopping
        </Link>
      </div>
    )
  } else {
    return (
      <>
        <div></div>
        <Container className="section-pad">
          <Row>
            <Col xs="12" md="7" lg={8} className="">
              <GuestCheckoutPanel />
              {!props.session?.punchout_buyer && <CheckoutAddressSelection />}
              <ProductionTimeBanner cartItems={props.cartItems} />
              <CartItemsList isCheckout={false} />
            </Col>

            <Col xs={12} md={5} lg={4}>
              <OrderSummary isCheckout={false} />
            </Col>
          </Row>
        </Container>
      </>
    )
  }
}

const mapStateToProps = state => {
  const {
    storeViews: { current: currentLocation },
    cartItems,
    portal,
    checkout: { errors, cart_loading },
    userCredits,
    currentUser,
    session,
    checkoutSessionToken: { token: checkoutSessionToken },
    integrations,
    checkout,
  } = state

  return {
    currentLocation,
    cartItems,
    portal,
    errors,
    cart_loading,
    userCredits,
    currentUser,
    session,
    checkoutSessionToken,
    integrations,
    checkout
  }
}

export default compose(
  withRouter,
  connect(mapStateToProps, {
    deleteCartItem,
    dispatchSweetAlert,
    clearSweetAlert,
    setCheckoutErrorMessage,
    updateCartItems,
    returnCartItemsRequireApprovalError,
    createCheckoutSessionToken,
    restoreCheckoutSessionFromToken,
    createShippingGroupConfiguration,
    clearGuestAddresses,
    fetchIntegrations
  })
)(Cart)
